import React, {
  FC,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useDocuments } from 'hooks/useDocuments';
import { _t, lockBodyScroll } from 'utils';
import type { DocumentsModalItem } from 'utils';

import iconPdf from 'assets/img/docs-v4/icon-pdf.png';
import iconDownload from 'assets/img/docs-v4/icon-download.png';
import checklistPdf from 'assets/docs/checklist-counterparties.pdf';
import presentationPdf from 'assets/docs/presentation-mosprom.pdf';
import buyerProgramPdf from 'assets/docs/buyer-program-2026.pdf';
import adeptPdf from 'assets/docs/adept7-test.pdf';

import styles from './styles.module.scss';

type Props = {
  onClose: () => void;
  items?: DocumentsModalItem[];
};

const LOCAL_DOCS: DocumentsModalItem[] = [
  {
    id: 'checklist',
    title: 'Чек-лист по проверке контрагентов',
    href: checklistPdf,
  },
  {
    id: 'presentation',
    title: 'Презентация АНО Моспром',
    href: presentationPdf,
  },
  {
    id: 'buyer',
    title: 'Байерская программа АНО Моспром 2026',
    href: buyerProgramPdf,
  },
  {
    id: 'adept',
    title: 'ADEPT7. Тест экспортной готовности',
    href: adeptPdf,
  },
];

const stripHtml = (value?: string) => (
  value ? value.replace(/(<([^>]+)>)/gi, '') : ''
);

const DocumentsModal: FC<Props> = ({ onClose, items: forcedItems }) => {
  const { documents, loading, error } = useDocuments();

  useEffect(() => lockBodyScroll(), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = useMemo((): DocumentsModalItem[] => {
    if (forcedItems?.length) {
      return forcedItems.map((item) => ({
        ...item,
        title: _t(item.title),
        downloadLabel: item.downloadLabel ? _t(item.downloadLabel) : undefined,
      }));
    }

    if (error || (!loading && !documents.length)) {
      return LOCAL_DOCS.map((item) => ({
        ...item,
        title: _t(item.title),
      }));
    }

    return documents.map((doc) => ({
      id: doc.id,
      title: stripHtml(doc.description) || doc.title,
      href: doc.file_url || doc.file || '#',
    }));
  }, [documents, error, forcedItems, loading]);

  return createPortal(
    (
      <div className={styles.root} role="dialog" aria-modal="true" aria-label={_t('Смотреть вложения')}>
        <button
          type="button"
          className={styles.backdrop}
          aria-label={_t('Закрыть')}
          onClick={onClose}
        />
        <aside className={styles.panel}>
          <div className={styles.head}>
            <h2 className={styles.title}>{_t('Смотреть вложения')}</h2>
            <button
              type="button"
              className={styles.close}
              aria-label={_t('Закрыть')}
              onClick={onClose}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>

          <ul className={styles.list}>
            {items.map((doc) => (
              <li key={doc.id} className={styles.item}>
                <div className={styles.docHead}>
                  <p className={styles.docTitle}>{doc.title}</p>
                  <img
                    src={iconPdf}
                    alt="PDF"
                    className={styles.pdfIcon}
                    width={41}
                    height={48}
                  />
                </div>
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.download}
                >
                  <span className={styles.downloadText}>
                    {doc.downloadLabel || _t('Скачать документ (PDF)')}
                  </span>
                  <img
                    src={iconDownload}
                    alt=""
                    width={20}
                    height={21}
                    className={styles.downloadIcon}
                  />
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    ),
    document.body,
  );
};

export default DocumentsModal;
